import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import useNotify from '../../hooks/useNotify';

import { gql, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql/language/ast';
import Form from '../Form/Form';
import ContactProfileForm from '../Profile/ContactProfileForm';
import { ContactProfileInput } from '../Profile/types';
import UserLoginForm, { UserLoginInput } from './UserLoginForm';
import {
  TESTER_CREDENTIALS,
  TESTER_GAUGES,
  TESTER_PROFILE,
} from '../../modes/individual/pages/profile/TesterProfile.query';
import FormColumn from '../Form/FormColumn';
import UploadSelectPreview from '../File/UploadSelectPreview';
import useFileUpload from '../../hooks/useFileUpload';
import { Label } from '@rebass/forms';
import { TempFile } from '../../hooks/useUploadTempFile';

const UPDATE_MYSELF = gql`
  ${TESTER_PROFILE}
  ${TESTER_CREDENTIALS}
  ${TESTER_GAUGES}
  mutation UpdateMyself($input: MyProfileInput!) {
    updateMyself(input: $input) {
      ...TesterProfile
      credentials(where: { archived: null }) {
        ...TesterCredentials
      }
      backflowTester {
        gauges(where: { archived: null }) {
          ...TesterGauges
        }
      }
    }
  }
`;

type Self = ContactProfileInput & {
  user: {
    email: string;
  };
  image?: {
    contentType: string;
    size: number;
    thumb: string;
    url: string;
  };
};

type SelfInput = ContactProfileInput & {
  user: {
    email: string;
    password: string;
  };
};

type SelfEditModalProps = {
  onClose: () => void;
  // user: UserItem | UserItemAdmin | SelfEditUser
  profile: Self;
  refetch?: () => void;
  refetchQueries?: DocumentNode[];
  hide?: (keyof ContactProfileInput)[];
  hideLogin?: boolean;
};

const SelfEditModal: React.FC<SelfEditModalProps> = ({
  onClose,
  profile,
  refetch,
  refetchQueries,
  hide,
  hideLogin,
}) => {
  const [profileInput, setProfileInput] = useState<SelfInput>({
    type: profile.type || '',
    name: profile.name || '',
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    // add new fields
    contactAddressId: profile.contactAddressId || null,
    phones:
      profile.phones.map((phone) => ({
        ...phone,
        __typename: undefined,
        id: undefined,
      })) || [],
    emails:
      profile.emails.map((email) => ({
        ...email,
        __typename: undefined,
        id: undefined,
      })) || [],
    user: {
      email: profile.user.email ?? '',
      password: '',
    },
  });

  const accept = {
    'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
  };
  const dropZone = useFileUpload({
    multiple: false,
    accept,
  });
  const { doUpload, acceptedFile, loading } = dropZone;

  const [confirmPassword, setConfirmPassword] = useState('');

  const { showError, showSuccess } = useNotify();

  const [updateMyself, { loading: updateLoading }] = useMutation(
    UPDATE_MYSELF,
    {
      onError: (error) => {
        showError('Error updating user', error);
        console.error('Error updating user', error);
      },
      refetchQueries,
    }
  );

  const onProfileChange = useCallback<(profile: ContactProfileInput) => void>(
    (profile) => {
      setProfileInput((prevState) => ({
        ...prevState,
        ...profile,
      }));
    },
    [setProfileInput]
  );

  const onLoginChange = useCallback(
    (userLogin: UserLoginInput) => {
      setConfirmPassword(userLogin.confirmPassword);

      setProfileInput((prevState) => ({
        ...prevState,
        user: {
          email: userLogin.email,
          password: userLogin.password,
        },
      }));
    },
    [setConfirmPassword, setProfileInput]
  );

  const save = useCallback(async () => {
    if (profileInput.type === 'COMPANY' && !profileInput.name) {
      showError('A name must be added to save this user');
      return;
    }
    if (
      (profileInput.type === 'INDIVIDUAL' && !profileInput.firstName) ||
      (profileInput.type === 'INDIVIDUAL' && !profileInput.lastName)
    ) {
      showError('A first and last name must be added to save this user');
      return;
    }
    if (!profileInput.user.email) {
      showError('An email must be added to save this user');
      return;
    }
    // If Password and Confirm Password inputs do not match...
    if (
      profileInput.user.password &&
      profileInput.user.password !== confirmPassword
    ) {
      return;
    } else {
      let tempFile: TempFile | undefined;
      if (acceptedFile) {
        try {
          tempFile = (await doUpload())[0];
        } catch (e) {
          showError('Error uploading file', e as Error);
          return;
        }
      }

      await updateMyself({
        variables: { input: { ...profileInput, logoTempKey: tempFile?.key } },
        onCompleted: (data) => {
          showSuccess('Profile updated');
          refetch?.();
          onClose();
        },
      });
    }
  }, [profileInput, acceptedFile, doUpload, updateMyself]);

  return (
    <Modal title="Edit Profile" onClose={onClose}>
      <ModalBody>
        <Form>
          <FormColumn>
            <FormColumn>
              <Label>Logo / Avatar</Label>
              <UploadSelectPreview
                icon="image"
                previewBackgroundColor="#fff"
                accept={accept}
                existingFile={profile.image}
                uploadState={dropZone}
                size={150}
              />
            </FormColumn>
            <ContactProfileForm
              onChange={onProfileChange}
              value={profileInput}
              hide={hide}
            />
            {!hideLogin && (
              <UserLoginForm
                onChange={onLoginChange}
                value={{ ...profileInput.user, confirmPassword }}
              />
            )}
          </FormColumn>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" disabled={updateLoading || loading} onClick={save}>
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SelfEditModal.propTypes = {
  onClose: PropTypes.any,
  profile: PropTypes.any,
};
export default SelfEditModal;
